/**
 * ป้ายกำกับและมุมมองของหน้าจอคิวข้อขัดแย้ง Offline Sync
 *
 * แยกออกจาก component ด้วยเหตุผลเดียวกับ `system/systemRolesView.ts`: คำที่ผู้ใช้
 * อ่านและการแปล token เป็นภาษาคน ทดสอบได้ตรง ๆ โดยไม่ต้องเรนเดอร์ทั้งหน้าจอ
 *
 * **กติกาข้อเดียวที่ห้ามผ่อน: token ที่ไม่รู้จักต้องแสดงดิบ ๆ** ไม่ใช่กลายเป็น
 * "ไม่ทราบสาเหตุ" — `reason` คือกฎที่เซิร์ฟเวอร์ปฏิเสธคำสั่งนั้นไว้ และ rule ใหม่ที่
 * ฝั่งเซิร์ฟเวอร์เพิ่มเข้ามาต้องเดินทางถึงคนที่ต้องตัดสินเสมอ แม้หน้าจอจะยังไม่มีคำ
 * แปลของมันก็ตาม
 */

import type { StatusTone } from '../StatusBadge';
import { shopFloorRuleText } from '../../lib/shopFloor/ruleText';
import {
  CONFLICT_REJECTED,
  CONFLICT_RESOLVED,
  type OfflineSyncConflict,
} from '../../lib/offlineConflicts/types';

/** query parameter ที่จำมุมมองไว้ใน URL — มุมมองที่บุ๊กมาร์กและส่งต่อกันได้ */
export const RESOLVED_PARAM = 'resolved';

/** อ่านมุมมองจาก URL — อะไรที่ไม่ใช่ `true` แท้ ๆ คือคิวที่ยังเปิดอยู่ */
export function showsResolved(params: URLSearchParams): boolean {
  return params.get(RESOLVED_PARAM) === 'true';
}

/** เปิดอยู่ = ยังไม่มีใครตัดสิน — `resolved_at` คือทั้งหมดของการแยกนี้ (D-074) */
export function isOpenConflict(conflict: OfflineSyncConflict): boolean {
  return conflict.resolved_at === null;
}

/* ---------------------------------------------------------- command type -- */

const COMMAND_TYPE_LABELS: Readonly<Record<string, string>> = {
  STAGING_TRANSFER: 'จัดเตรียมวัตถุดิบ (STAGING_TRANSFER)',
  SCAN_IN: 'เริ่มงาน (SCAN_IN)',
  SCAN_OUT: 'ปิดงาน (SCAN_OUT)',
};

export function commandTypeLabel(commandType: string): string {
  return COMMAND_TYPE_LABELS[commandType] ?? commandType;
}

/* ---------------------------------------------------------------- reason -- */

/**
 * คำอธิบายของ rule token ที่พบบ่อย — **คำแปล ไม่ใช่การตีความ**
 *
 * ข้อความแต่ละบรรทัดบอกสิ่งที่เกิดขึ้นกับคำสั่งนั้น ไม่ได้บอกว่าต้องกดปุ่มไหน: การ
 * ตัดสินเป็นของคนที่อ่าน และหน้าจอนี้ไม่มีข้อมูลพอจะแนะนำแทนเขาได้
 */
const REASON_LABELS: Readonly<Record<string, string>> = {
  CLIENT_EVENT_ID_REUSED: 'เครื่องส่งรหัสคำสั่งเดิมพร้อมข้อมูลที่ต่างออกไป',
  DEVICE_SEQUENCE_NO_REUSED: 'ลำดับคำสั่งของเครื่องนี้ซ้ำกับคำสั่งอื่น',
  DEVICE_NOT_ACTIVE: 'เครื่องต้นทางไม่ได้อยู่ในสถานะใช้งาน',
  MALFORMED_PAYLOAD: 'ข้อมูลของคำสั่งไม่ตรงรูปแบบที่ระบบรับได้',
  UNKNOWN_COMMAND_TYPE: 'ชนิดคำสั่งนี้ไม่มีอยู่ในระบบ',
  PERMISSION_DENIED: 'แผนกของผู้สแกนไม่มีสิทธิ์ทำคำสั่งนี้',
  PLANT_OUTSIDE_SCOPE: 'คำสั่งอ้างถึงโรงงานนอกขอบเขตของผู้สแกน',
  NO_OPEN_CONFIRMATION: 'ไม่มี cycle ที่เปิดค้างอยู่ให้ปิด',
  CONFIRMATION_ALREADY_OPEN: 'มี cycle ที่เปิดค้างอยู่แล้ว',
  INSUFFICIENT_AVAILABLE_STOCK: 'สต๊อกที่ใช้ได้ไม่พอตามจำนวนที่สแกน',
  NO_STAGED_LOTS: 'ยังไม่มีวัตถุดิบที่จัดเตรียมไว้ให้เริ่มงาน',
  NO_STAGED_LOTS_TO_CONSUME: 'ไม่เหลือวัตถุดิบที่จัดเตรียมไว้ให้ตัดออก',
  LOT_NOT_QUALITY_RELEASED: 'lot ที่อ้างถึงยังไม่ถูกปล่อยผ่านคุณภาพ',
  ORDER_NOT_SCANNABLE: 'ใบสั่งผลิตไม่ได้อยู่ในสถานะที่สแกนได้',
  ORDER_NOT_STAGEABLE: 'ใบสั่งผลิตไม่ได้อยู่ในสถานะที่จัดเตรียมได้',
  WORK_CENTER_MISMATCH: 'หน่วยผลิตที่สแกนไม่ตรงกับที่ระบุไว้ในใบสั่งผลิต',
};

/** คำอธิบายของเหตุผล — `null` เมื่อยังไม่มีคำแปล (หน้าจอแสดง token ดิบแทน) */
export function reasonDescription(reason: string): string | null {
  return REASON_LABELS[reason] ?? shopFloorRuleText(reason);
}

/**
 * กฎที่ทำให้ **คำสั่งนี้ลงไม่ได้ไม่ว่าจะส่งซ้ำอีกกี่ครั้ง** (taxonomy ของ D-076)
 *
 * แยกโทนออกจากกฎธุรกิจที่แก้เหตุแล้วสแกนใหม่ได้ เพราะสองกลุ่มนี้ต้องการการกระทำที่
 * ต่างกันโดยสิ้นเชิง — กลุ่มนี้ต้องไปแก้ที่เครื่อง/ข้อมูล ส่วนอีกกลุ่มต้องไปแก้ที่
 * หน้างานแล้วสแกนใหม่
 */
const UNAPPLIABLE_REASONS: readonly string[] = [
  'CLIENT_EVENT_ID_REUSED',
  'DEVICE_NOT_ACTIVE',
  'DEVICE_SEQUENCE_NO_REUSED',
  'MALFORMED_PAYLOAD',
  'UNKNOWN_COMMAND_TYPE',
];

export function reasonTone(reason: string): StatusTone {
  return UNAPPLIABLE_REASONS.includes(reason) ? 'danger' : 'warning';
}

/* ------------------------------------------------------------ resolution -- */

/** ป้ายของผลการตัดสิน — `null` แปลว่ายังไม่มีใครตัดสิน */
export function resolutionLabel(resolution: string | null): string | null {
  switch (resolution) {
    case CONFLICT_RESOLVED:
      return 'จัดการแล้ว (RESOLVED)';
    case CONFLICT_REJECTED:
      return 'ไม่ทำคำสั่งนี้ (REJECTED)';
    case null:
      return null;
    default:
      // token ที่หน้าจอยังไม่รู้จักต้องอ่านออกอยู่ดี ไม่ใช่หายไปเฉย ๆ
      return resolution;
  }
}

export function resolutionTone(resolution: string | null): StatusTone {
  switch (resolution) {
    case CONFLICT_RESOLVED:
      return 'success';
    case CONFLICT_REJECTED:
      return 'neutral';
    default:
      return 'info';
  }
}
